import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getChain } from "../api/chains";
import { useAuth } from "./useAuth";
import type { Chain } from "../types/chain";

const lastModifiedByViewerChain = new Map<string, string>();

const validatorKey = (characterId: number, chainId: string) =>
  `${characterId}:${chainId}`;

export function useChain(id: string | null) {
  const queryClient = useQueryClient();
  const auth = useAuth();
  const characterId = auth.data?.character_id ?? null;

  return useQuery<Chain>({
    queryKey: ["chains", characterId, id],
    queryFn: async () => {
      const chainId = id!;
      const viewerId = characterId!;
      const key = validatorKey(viewerId, chainId);
      const ims = lastModifiedByViewerChain.get(key) ?? null;
      let res = await getChain(chainId, ims);
      if (res.notModified) {
        const cached = queryClient.getQueryData<Chain>([
          "chains",
          viewerId,
          chainId,
        ]);
        if (cached) return cached;
        lastModifiedByViewerChain.delete(key);
        res = await getChain(chainId, null);
      }
      if (res.lastModified) {
        lastModifiedByViewerChain.set(key, res.lastModified);
      }
      if (!res.chain) {
        throw new Error("chain payload missing");
      }
      return res.chain;
    },
    enabled: id != null && characterId != null,
    refetchInterval: 5000,
    refetchOnWindowFocus: true,
  });
}
